import { Rectangle, Texture, type TextureSource } from 'pixi.js';
import { corsUrl, loadBitmap, type AssetCatalog } from './assets';
import { wingAtlas } from './character-sheet';
import { WING_MOTION, type WingMotionSide } from './wing-motion';

interface RawFrame {
  frame: { x: number; y: number; w: number; h: number };
  spriteSourceSize?: { x: number; y: number; w: number; h: number };
  sourceSize?: { w: number; h: number };
}

/**
 * Onde a origem das asas fica no quadro do personagem na partida, em px da célula de 114 × 95. O legado coloca o corpo em (-62, -83) da origem do
 * personagem (`GameCharacter`) e as asas em `(weaponX, weaponY)` = (-62, -83): o canto da célula. O ponto de registro de cada arte vem do `WING_MOTION`.
 */
export const WING_ANCHOR = { x: 0, y: 0 } as const;

/** Uma asa na partida: os quadros de espera e de tiro e, se bate, os quadros do clipe com a matriz de cada estado. */
export interface WingSide {
  /** Estático: espera (0) e tiro (1). A textura já leva o deslocamento do exportador: a origem dela é o canto da tela do símbolo. */
  still: [Texture, Texture];
  /** Canto da tela do símbolo, em px a partir da origem das asas. */
  at: readonly [number, number];
  flap?: {
    frames: Texture[];
    /** Canto da tela do clipe em px, dentro da matriz do clipe. */
    at: readonly [number, number];
    /** Matriz do clipe na espera e no tiro: `[a, b, c, d, tx, ty]`. */
    matrix: readonly (readonly [number, number, number, number, number, number])[];
  };
}

/** Asas na partida: a esquerda (atrás do corpo) e a direita (à frente). */
export interface WingArt {
  left: WingSide;
  right: WingSide;
}

/**
 * Arte das asas na partida, já convertida do `wings.swf` do legado: as classes `gameWingIn` (esquerda) e `gameWingOut` (direita) têm o quadro 1
 * (espera) e o 2 (tiro), e o clipe `movie` de cada uma bate as asas (19 quadros). Cada asa baixa uma vez. Asa sem arte devolve `null`.
 */
export class WingArtLibrary {
  private readonly cache = new Map<string, Promise<WingArt | null>>();
  private readonly ready = new Map<string, WingArt | null>();

  constructor(private readonly assets: AssetCatalog) {}

  load(pic: string): Promise<WingArt | null> {
    let cached = this.cache.get(pic);
    if (!cached) {
      cached = this.build(pic)
        .catch(() => null)
        .then((art) => {
          this.ready.set(pic, art);
          return art;
        });
      this.cache.set(pic, cached);
    }
    return cached;
  }

  /** Arte já carregada; `undefined` enquanto baixa (e dispara o download) e `null` se a asa não tem arte. */
  peek(pic: string): WingArt | null | undefined {
    if (!this.cache.has(pic)) void this.load(pic);
    return this.ready.get(pic);
  }

  private texture(source: TextureSource, f: RawFrame): Texture {
    const full = f.sourceSize ?? { w: f.frame.w, h: f.frame.h };
    const offset = f.spriteSourceSize ?? { x: 0, y: 0, w: f.frame.w, h: f.frame.h };
    return new Texture({
      source,
      frame: new Rectangle(f.frame.x, f.frame.y, f.frame.w, f.frame.h),
      orig: new Rectangle(0, 0, full.w, full.h),
      trim: new Rectangle(offset.x, offset.y, f.frame.w, f.frame.h),
    });
  }

  private async build(pic: string): Promise<WingArt | null> {
    const motion = WING_MOTION[pic]?.g;
    const json = wingAtlas(pic);
    const webp = json.replace(/\.json$/, '.webp');
    if (!motion || !this.assets.has(json) || !this.assets.has(webp)) return null;
    const response = await fetch(corsUrl(this.assets.url(json)));
    if (!response.ok) return null;
    const atlas = (await response.json()) as { frames: Record<string, RawFrame> };
    const bitmap = await loadBitmap(this.assets.url(webp));
    if (!bitmap) return null;
    const source = Texture.from(bitmap).source;
    const side = (data: WingMotionSide): WingSide | null => {
      const one = atlas.frames[`${data.n}/1`];
      if (!one) return null;
      const two = atlas.frames[`${data.n}/2`] ?? one;
      const result: WingSide = { still: [this.texture(source, one), this.texture(source, two)], at: data.t };
      if (data.v) {
        const frames: Texture[] = [];
        for (let k = 1; k <= data.v.f; k++) {
          const f = atlas.frames[`${data.v.n}/${k}`];
          if (f) frames.push(this.texture(source, f));
        }
        if (frames.length > 0) result.flap = { frames, at: data.v.t, matrix: data.v.m };
      }
      return result;
    };
    const left = side(motion.l);
    const right = side(motion.r);
    return left && right ? { left, right } : null;
  }
}
